// Single source of truth mapping a magviz theme → CSS custom-property names.
// Used in two places:
//   - Runtime: src/ui/themeVars.ts iterates and calls setProperty on :root.
//   - Build time: the Vite plugin in vite.config.ts calls renderRootVars()
//     to inline a <style> block before css/style.css, eliminating the
//     blueish first-paint flash.
//
// Pure functions, no DOM access — safe to import in Node.

import type { ThemeDefaults } from '../config/defaults';

type PartialUI = Partial<ThemeDefaults['ui']>;
type PartialFont = Partial<ThemeDefaults['font']>;
type PartialTheme = { ui?: PartialUI; font?: PartialFont } | Record<string, unknown> | null | undefined;

// Magviz brand identity. Theme files can override `accent`, but these are
// the magviz-blue values used whenever a theme is silent. One source of
// truth — defaults.ts and pypicThemeMap.ts import from here.
export const MAGVIZ_BRAND = {
    accent: '#3a6eff',
    blueSoft: [90, 140, 250] as const,   // panel/tp/scrollbar borders
    blueGlow: [120, 170, 255] as const,  // slider track + scrollbar thumb
} as const;

export const rgbaBrand = (rgb: readonly [number, number, number], a: number): string =>
    `rgba(${rgb[0]}, ${rgb[1]}, ${rgb[2]}, ${a})`;

const FALLBACK_UI: ThemeDefaults['ui'] = {
    bodyBg: '#000000',
    textColor: '#ffffff',
    tweakTextColor: '#cfe0ff',
    panelBg: 'rgba(15, 20, 35, 0.38)',
    panelBorder:           rgbaBrand(MAGVIZ_BRAND.blueSoft, 0.22),
    panelHeaderBg:         'rgba(30, 40, 70, 0.3)',
    panelHeaderBorder:     rgbaBrand(MAGVIZ_BRAND.blueSoft, 0.30),
    perfBg:                'rgba(15, 20, 35, 0.5)',
    helpBg:                'rgba(15, 20, 35, 0.6)',
    helpBorder:            rgbaBrand(MAGVIZ_BRAND.blueSoft, 0.30),
    inputBg:               'rgba(24, 30, 50, 0.9)',
    buttonBg:              'rgba(58, 110, 255, 0.25)',     // tied to accent #3a6eff (= 58,110,255)
    buttonHoverBg:         'rgba(74, 126, 255, 0.35)',
    buttonText:            '#dfe9ff',
    paneBorder:              rgbaBrand(MAGVIZ_BRAND.blueSoft, 0.18),
    sliderTrack:           rgbaBrand(MAGVIZ_BRAND.blueGlow, 0.32),
    scrollbarTrack:        'rgba(30, 40, 70, 0.10)',
    scrollbarThumb:        rgbaBrand(MAGVIZ_BRAND.blueGlow, 0.32),
    scrollbarThumbBorder:  rgbaBrand(MAGVIZ_BRAND.blueGlow, 0.22),
    scrollbarThumbHover:   rgbaBrand(MAGVIZ_BRAND.blueGlow, 0.42),
    accent:                MAGVIZ_BRAND.accent,
    thumbBorder:           '#ffffff',
    sliderFill:            MAGVIZ_BRAND.accent,
    sliderGrip:            MAGVIZ_BRAND.accent,
    sliderTick:            rgbaBrand(MAGVIZ_BRAND.blueGlow, 0.25),
    sliderRadius:          '7px',
    sliderHeight:          '3px',
    sliderGripSize:        '21px',
};

const FALLBACK_FONT: ThemeDefaults['font'] = {
    family: ['Segoe UI', 'Arial', 'sans-serif'],
    sizeTitle: 16,
    sizeLabel: 13,
    sizeTick: 12,
    sizeOverlay: 13,
};

// rgba(r, g, b, a) → rgba(r, g, b, alpha) with the alpha bumped.
function bumpAlpha(value: string | undefined, alpha: number): string | undefined {
    if (!value) return undefined;
    const m = value.match(/^rgba?\(([^)]+)\)$/);
    const inner = m && m[1];
    if (!inner) return value;
    const parts = inner.split(',').map((s) => s.trim());
    if (parts.length < 3) return value;
    return `rgba(${parts[0]}, ${parts[1]}, ${parts[2]}, ${alpha})`;
}

function cssFontFamily(family: string[]): string {
    return family.map((f) => (/\s/.test(f) ? `"${f}"` : f)).join(', ');
}

/** Map a (partial) theme to its CSS custom-property entries. Always returns
 * a complete set — missing fields fall back to the dark-ish defaults that
 * mirror THEME_DEFAULTS in src/config/defaults.ts. */
export function themeToCssVars(theme: PartialTheme): Record<string, string> {
    const t = (theme || {}) as { ui?: PartialUI; font?: PartialFont };
    const ui = { ...FALLBACK_UI, ...(t.ui || {}) };
    const font = { ...FALLBACK_FONT, ...(t.font || {}) };

    const panelBgStrong = bumpAlpha(ui.panelBg, 0.65) || ui.panelBg;
    const tooltipBg = ui.panelBg;
    const tooltipBorder = ui.panelBorder;
    const tooltipText = ui.tweakTextColor || ui.textColor;
    const hoverTooltipBg = bumpAlpha(ui.panelBg, 0.36) || ui.panelBg;
    const hoverTooltipBorder = bumpAlpha(ui.panelBorder, 0.22) || ui.panelBorder;
    const hoverTooltipText = ui.textColor;

    return {
        '--magviz-body-bg': ui.bodyBg,
        '--magviz-panel-bg': ui.panelBg,
        '--magviz-panel-bg-strong': panelBgStrong,
        '--magviz-panel-border': ui.panelBorder,
        '--magviz-panel-header-bg': ui.panelHeaderBg,
        '--magviz-panel-header-border': ui.panelHeaderBorder,
        '--magviz-perf-bg': ui.perfBg,
        '--magviz-help-bg': ui.helpBg,
        '--magviz-help-border': ui.helpBorder,
        '--magviz-input-bg': ui.inputBg,
        '--magviz-pane-border': ui.paneBorder,

        '--magviz-text': ui.textColor,
        '--magviz-text-muted': ui.tweakTextColor,
        '--magviz-accent': ui.accent,
        '--magviz-button-bg': ui.buttonBg,
        '--magviz-button-bg-hover': ui.buttonHoverBg,
        '--magviz-button-text': ui.buttonText,
        '--magviz-thumb-border': ui.thumbBorder,

        '--magviz-slider-track': ui.sliderTrack,
        '--magviz-slider-fill': ui.sliderFill,
        '--magviz-slider-grip': ui.sliderGrip,
        '--magviz-slider-tick': ui.sliderTick,
        '--magviz-slider-radius': ui.sliderRadius,
        '--magviz-slider-height': ui.sliderHeight,
        '--magviz-slider-grip-size': ui.sliderGripSize,
        '--magviz-scrollbar-track': ui.scrollbarTrack,
        '--magviz-scrollbar-thumb': ui.scrollbarThumb,
        '--magviz-scrollbar-thumb-border': ui.scrollbarThumbBorder,
        '--magviz-scrollbar-thumb-hover': ui.scrollbarThumbHover,

        '--magviz-tooltip-bg': tooltipBg,
        '--magviz-tooltip-border': tooltipBorder,
        '--magviz-tooltip-text': tooltipText,
        '--magviz-hover-tooltip-bg': hoverTooltipBg,
        '--magviz-hover-tooltip-border': hoverTooltipBorder,
        '--magviz-hover-tooltip-text': hoverTooltipText,

        '--magviz-font-family': cssFontFamily(font.family),
        '--magviz-font-size-title': `${font.sizeTitle}px`,
        '--magviz-font-size-label': `${font.sizeLabel}px`,
        '--magviz-font-size-tick': `${font.sizeTick}px`,
        '--magviz-font-size-overlay': `${font.sizeOverlay}px`,
    };
}

/** Render the same map as a `:root { … }` CSS block — suitable for inlining
 * in `<head>` so first paint already has theme values. */
export function renderRootVars(theme: PartialTheme): string {
    const vars = themeToCssVars(theme);
    const body = Object.entries(vars)
        .map(([k, v]) => `  ${k}: ${v};`)
        .join('\n');
    return `:root {\n${body}\n}\n`;
}
